/** Section 4 — fan screen wording, EN and FR, with one "Reset to default" per
 *  language. The scratch instruction is NOT here: it already exists in the
 *  Gift Generator setup copy and is edited in the Branding section. */
import { useEffect, useState } from 'react';
import { Loader2, RotateCcw, Save } from 'lucide-react';
import { toast } from 'sonner';
import { CopyField, autoTranslate, builtIn, isAuto, mergeCopy } from './CopyFields';
import api from '../../../lib/api';
import { btn } from './buttons';

const FIELDS = [
    ['winTitle', 'Win — title', false],
    ['winSubtitle', 'Win — subtitle', false],
    ['loseTitle', 'Lose — title', false],
    ['loseBody', 'Lose — message', true],
    ['noRoundTitle', 'No goal right now — title', false],
    ['noRoundBody', 'No goal right now — message', true],
    ['missedTitle', 'You missed this one — title', false],
    ['missedBody', 'You missed this one — message', true],
    ['setupButton', 'Set up my table — button', false],
    ['pinChooseLabel', 'Choose a PIN — label', false],
    ['pinEnterTitle', 'Enter your table PIN — title', false],
    ['pinMismatch', "That PIN doesn't match", false],
    ['pinLocked', 'Too many tries (use {{seconds}} for the countdown)', true],
    ['tablePlayedTitle', 'Your table already played this goal', false],
    ['tonightButton', "Tonight's contest — button label", false],
    ['statsButton', "Tonight's stats — button label", false],
    ['statsTitle', "Tonight's stats — panel title", false],
];
const KEYS = FIELDS.map(([k]) => k);

const pick = (copy) => ({
    en: Object.fromEntries(KEYS.map((k) => [k, (copy?.en || {})[k] || ''])),
    fr: Object.fromEntries(KEYS.map((k) => [k, (copy?.fr || {})[k] || ''])),
});

export const GoalWordingBlock = ({ cfg, onSave, onDraft }) => {
    const [draft, setDraft] = useState(() => pick(cfg.copy));
    const [busy, setBusy] = useState(false);
    const [auto, setAuto] = useState(cfg.copy_auto_fr || {});
    const [failed, setFailed] = useState([]);

    useEffect(() => { setDraft(pick(cfg.copy)); setAuto(cfg.copy_auto_fr || {}); }, [cfg]);
    // Typed wording shows in the phone preview before it is saved.
    useEffect(() => { onDraft?.(draft); }, [draft, onDraft]);

    const change = (lang, field, value) =>
        setDraft((p) => ({ ...p, [lang]: { ...p[lang], [field]: value } }));

    const applyTranslation = (out) => {
        if (!out) return;
        setFailed(out.failed || []);
        if (!(out.translated || []).length) return;
        setAuto(out.copy_auto_fr || {});
        setDraft((p) => ({ ...p, fr: { ...p.fr, ...pick(out.copy).fr } }));
        toast.success('French filled in automatically');
    };

    const run = async (next, translate = true) => {
        setBusy(true);
        // Only the ENGLISH fields this save actually changed.
        const keys = KEYS.filter((k) => (next.en[k] || '') !== ((cfg.copy?.en || {})[k] || ''));
        try { await onSave({ copy: mergeCopy(cfg.copy, next) }); }
        finally { setBusy(false); }
        if (translate) applyTranslation(await autoTranslate(api, cfg, keys));
    };

    const retranslate = async (field) => {
        if (!window.confirm('Replace the French text of this field with a fresh translation of the English?')) return;
        applyTranslation(await autoTranslate(api, cfg, [field], true));
    };

    const resetLang = async (lang) => {
        if (!window.confirm(`Clear every ${lang.toUpperCase()} override and go back to the built-in text?`)) return;
        const cleared = { ...draft, [lang]: Object.fromEntries(KEYS.map((k) => [k, ''])) };
        setDraft(cleared);
        await run(cleared, false);
    };

    return (
        <div className="space-y-4 pt-3">
            <p className="text-xs text-slate-500">
                Leave a field empty to use the built-in text — the grey placeholder shows exactly
                what the fan will see.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
                {FIELDS.map(([key, label, isArea]) => ([
                    <CopyField key={`${key}-en`} label={label} lang="en" field={key} textarea={isArea}
                               value={draft.en[key]} onChange={change}
                               placeholder={builtIn('en', key) || '(nothing shown)'}
                               testid={`gg-copy-${key}-en`} />,
                    <CopyField key={`${key}-fr`} label={label} lang="fr" field={key} textarea={isArea}
                               value={draft.fr[key]} onChange={change}
                               placeholder={builtIn('fr', key) || '(nothing shown)'}
                               testid={`gg-copy-${key}-fr`}
                               auto={isAuto({ copy_auto_fr: auto }, key, draft.fr[key])}
                               failed={failed.includes(key)}
                               onRetranslate={retranslate} />,
                ]))}
            </div>
            <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => run(draft)} disabled={busy} data-testid="gg-wording-save"
                        className={btn.primary}>
                    {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                    Save wording
                </button>
                <button type="button" onClick={() => resetLang('en')} disabled={busy} data-testid="gg-wording-reset-en"
                        className={btn.warning}>
                    <RotateCcw size={14} /> Reset EN to default
                </button>
                <button type="button" onClick={() => resetLang('fr')} disabled={busy} data-testid="gg-wording-reset-fr"
                        className={btn.warning}>
                    <RotateCcw size={14} /> Reset FR to default
                </button>
            </div>
        </div>
    );
};

export default GoalWordingBlock;
